import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable/Clickable'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import noop from 'lib/noop'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const HowLongGrid = styled.div`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr 1fr;

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

const HowLongGridItem = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(12)};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;
  gap: ${rem(4)};

  &:not(:disabled) {
    &:hover {
      border-color: ${props => props.theme.palette.neutral.one};
    }

    .input-keyboard &:focus {
      outline: 2px solid ${props => props.theme.palette.neutral.five};
      outline-offset: 2px;
    }

    &.selected {
      box-shadow: inset 0px 0px 0px 2px ${({ theme }) => theme.palette.neutral.one};
    }
  }


  &:disabled {
    color: ${props => props.theme.palette.neutral.four};
  }
`

interface CruiseDurationOption {
  name: string;
  description: string;
  min: number | null;
  max: number | null;
}

export const CruiseDurationOptions: Array<CruiseDurationOption> = [
  { name: 'Any duration', description: 'I’ll decide later', min: null, max: null },
  { name: 'Short trip', description: '7 days or less', min: 1, max: 7 },
  { name: 'Medium trip', description: '8 - 14 days', min: 8, max: 14 },
  { name: 'Long trip', description: '15 days or more', min: 15, max: null },
]

interface Props {
  onSelect?: () => void;
  onChange?: (value: App.CruiseGlobalFilters) => void;
}

function CruiseWhenHowLong(props: Props) {
  const { onSelect = noop, onChange } = props
  const { durationMin, durationMax } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const selectedItem = useMemo(() =>
    CruiseDurationOptions.find(opt => opt.min >= durationMin && opt.max <= durationMax),
  [durationMax, durationMin])

  const handlerSearch = useCallback((option: CruiseDurationOption) => {
    if (option === selectedItem) {
      // Same option, guess we toggled off, go back to empty
      searchDispatch({ type: GlobalSearchStateActions.SET_DURATION_RANGE, min: undefined, max: undefined })
      if (onChange) onChange({ durationMin: null, durationMax: null })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.SET_DURATION_RANGE, min: option.min, max: option.max })
      onSelect()
      if (onChange) onChange({ durationMin: option.min, durationMax: option.max })
    }
  }, [onSelect, searchDispatch, selectedItem, onChange])

  return <VerticalSpacer gap={16}>
    <Heading align="center" variant="heading6">How long?</Heading>
    <HowLongGrid>
      {CruiseDurationOptions.map((option) => {
        return <HowLongGridItem
          key={option.name}
          onClick={() => handlerSearch(option)}
          className={option === selectedItem ? 'selected' : ''}
        >
          <BodyText variant="medium" weight="bold">{option.name}</BodyText>
          <BodyText variant="small">{option.description}</BodyText>
        </HowLongGridItem>
      })}
    </HowLongGrid>
  </VerticalSpacer>
}

export default CruiseWhenHowLong
